import React, { useState } from 'react';
import { Save, X, Upload } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Artist } from '../../types';

interface ArtistFormProps {
  artist?: Artist;
  isOpen: boolean;
  onClose: () => void;
  onSave: (artistData: Partial<Artist>) => void;
}

export const ArtistForm: React.FC<ArtistFormProps> = ({
  artist,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Partial<Artist>>({
    name: artist?.name || '',
    bio: artist?.bio || '',
    image: artist?.image || '',
    socialLinks: {
      instagram: artist?.socialLinks.instagram || '',
      twitter: artist?.socialLinks.twitter || '',
      facebook: artist?.socialLinks.facebook || '',
      spotify: artist?.socialLinks.spotify || '',
      youtube: artist?.socialLinks.youtube || ''
    },
    contactInfo: {
      email: artist?.contactInfo.email || '',
      phone: artist?.contactInfo.phone || ''
    }
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string>(artist?.image || '');

  const updateFormData = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setImagePreview(result);
        updateFormData('image', result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Artist name is required';
    }
    
    if (!formData.bio?.trim()) {
      newErrors.bio = 'Artist bio is required';
    }
    
    if (!formData.image?.trim()) {
      newErrors.image = 'Artist image is required';
    }
    
    if (formData.contactInfo?.email && !/\S+@\S+\.\S+/.test(formData.contactInfo.email)) {
      newErrors['contactInfo.email'] = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      bio: '',
      image: '',
      socialLinks: {
        instagram: '',
        twitter: '',
        facebook: '',
        spotify: '',
        youtube: ''
      },
      contactInfo: {
        email: '',
        phone: ''
      }
    });
    setImagePreview('');
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={artist ? 'Edit Artist' : 'Add New Artist'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Basic Information
          </h3>
          
          <div className="space-y-4">
            <Input
              label="Artist Name"
              placeholder="Enter artist name"
              value={formData.name || ''}
              onChange={(value) => updateFormData('name', value)}
              error={errors.name}
              required
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Artist Bio <span className="text-red-400">*</span>
              </label>
              <textarea
                className="w-full px-3 py-2 bg-black-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all duration-200"
                rows={4}
                placeholder="Tell us about the artist..."
                value={formData.bio || ''}
                onChange={(e) => updateFormData('bio', e.target.value)}
              />
              {errors.bio && (
                <p className="text-sm text-red-400 mt-1">{errors.bio}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Artist Image */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Artist Image
          </h3>
          
          <div className="space-y-4">
            {imagePreview ? (
              <div className="flex items-center space-x-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-24 h-24 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <p className="text-sm text-green-400 mb-2">✓ Image uploaded</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('image-upload')?.click()}
                  >
                    Change Image
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-gold-500 hover:bg-gold-500/5 transition-all"
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-white mb-2">Upload artist image</p>
                <p className="text-sm text-gray-400">Click to browse or drag and drop</p>
              </div>
            )}
            
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            
            {errors.image && (
              <p className="text-sm text-red-400">{errors.image}</p>
            )}
          </div>
        </Card>

        {/* Contact Information */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Contact Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              placeholder="artist@example.com"
              value={formData.contactInfo?.email || ''}
              onChange={(value) => updateFormData('contactInfo.email', value)}
              error={errors['contactInfo.email']}
            />
            <Input
              label="Phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={formData.contactInfo?.phone || ''}
              onChange={(value) => updateFormData('contactInfo.phone', value)}
            />
          </div>
        </Card>

        {/* Social Media Links */}
        <Card>
          <h3 className="text-lg font-semibold text-white font-display mb-4">
            Social Media Links
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Instagram"
                placeholder="@username"
                value={formData.socialLinks?.instagram || ''}
                onChange={(value) => updateFormData('socialLinks.instagram', value)}
              />
              <Input
                label="Twitter"
                placeholder="@username"
                value={formData.socialLinks?.twitter || ''}
                onChange={(value) => updateFormData('socialLinks.twitter', value)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Spotify Artist ID"
                placeholder="artist-id"
                value={formData.socialLinks?.spotify || ''}
                onChange={(value) => updateFormData('socialLinks.spotify', value)}
              />
              <Input
                label="YouTube Channel"
                placeholder="channel-name"
                value={formData.socialLinks?.youtube || ''}
                onChange={(value) => updateFormData('socialLinks.youtube', value)}
              />
            </div>
            
            <Input
              label="Facebook"
              placeholder="facebook.com/artistname"
              value={formData.socialLinks?.facebook || ''}
              onChange={(value) => updateFormData('socialLinks.facebook', value)}
            />
          </div>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            icon={X}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            icon={Save}
          >
            {artist ? 'Update Artist' : 'Add Artist'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};